import React, { useCallback, useState } from 'react'
import { Button, ConfigProvider, Dropdown, Flex, Modal, message } from 'antd'
import type { MenuProps } from 'antd'
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons'
import { format, isValid, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import AuthSelectors from 'modules/domain/auth/selectors'
import { deleteSeedInventory } from 'modules/domain/seedTracker/managers'
import type { SeedInventory } from '../SeedInventoryTable'
import {
  SeedInventoryDrawerTabs,
  drawerChromeStyle,
  drawerLastUpdatedLabelStyle,
  drawerLastUpdatedStyle,
  drawerSeasonLabelStyle,
  drawerSeasonRowStyle,
  drawerTabPaneScrollableStyle,
  drawerTabsContainerStyle,
  drawerTopBarStyle,
  seedInventoryTabsBarStyles,
  seedInventoryTabsStyles,
} from '../../styles'
import SeedInventoryDetailsTab from './SeedInventoryDetailsTab'
import SeedInventoryOrdersTab from './SeedInventoryOrdersTab'
import SeedInventoryHistoryTab from './SeedInventoryHistoryTab'
import { PedigreeTag } from '../PedigreeTag'

function formatLastUpdatedDate(dateStr: string): string {
  const parsed = parseISO(dateStr)
  const date = isValid(parsed) ? parsed : new Date(dateStr)
  return isValid(date) ? format(date, 'dd MMMM yyyy') : dateStr
}

type SeedInventoryDrawerContentProps = {
  record?: SeedInventory | null
  onAdjustInventory?: () => void
  onDeleted?: () => void
  isAdjusting?: boolean
}

const SeedInventoryDrawerContent: React.FC<SeedInventoryDrawerContentProps> = ({
  record,
  onAdjustInventory,
  onDeleted,
  isAdjusting = false,
}) => {
  const { t } = useTranslation()
  const [activeKey, setActiveKey] = useState('details')
  const [modal, contextHolder] = Modal.useModal()
  const isAdmin = useSelector(AuthSelectors.isAdmin)

  const handleDelete = useCallback(() => {
    if (!record?.id || !isAdmin) {
      return
    }
    modal.confirm({
      title: t('seedTracker:detail.deleteInventoryConfirmTitle'),
      content: t('seedTracker:detail.deleteConfirmContent'),
      icon: <WarningOutlined />,
      okText: t('seedTracker:detail.deleteConfirmOk'),
      okButtonProps: { danger: true },
      cancelText: t('seedTracker:detail.deleteConfirmCancel'),
      onOk: async () => {
        try {
          await deleteSeedInventory(record.id)
          message.success(t('seedTracker:detail.deleteInventorySuccess'))
          onDeleted?.()
        } catch (error) {
          console.error(error)
          message.error(t('unknown_error', { ns: 'errors' }))
        }
      },
    })
  }, [isAdmin, modal, onDeleted, record?.id, t])

  const moreMenuItems: MenuProps['items'] = isAdmin
    ? [
        {
          key: 'delete',
          onClick: handleDelete,
          label: t('seedTracker:detail.deleteInventory'),
          icon: <DeleteOutlined />,
          danger: true,
        },
      ]
    : []

  const tabItems = [
    {
      key: 'details',
      label: t('detail.tabs.details', 'Details'),
      children: <SeedInventoryDetailsTab record={record} />,
      destroyOnHidden: true,
      style: drawerTabPaneScrollableStyle,
    },
    {
      key: 'orders',
      label: t('detail.tabs.orders', 'Orders'),
      children: <SeedInventoryOrdersTab record={record} />,
      destroyOnHidden: true,
      style: drawerTabPaneScrollableStyle,
    },
    {
      key: 'history',
      label: t('detail.tabs.history', 'History'),
      children: <SeedInventoryHistoryTab record={record} />,
      destroyOnHidden: true,
      style: drawerTabPaneScrollableStyle,
    },
  ]

  return (
    <Flex vertical style={drawerTabsContainerStyle}>
      {contextHolder}
      <Flex justify="space-between" align="flex-start" style={{ ...drawerTopBarStyle, ...drawerChromeStyle }}>
        <Flex align="center" gap={8} style={{ minWidth: 0, flexWrap: 'wrap' }}>
          {record?.grower ? (
            <div style={{ ...drawerLastUpdatedStyle, fontSize: 24, fontWeight: 700 }}>{record.grower}</div>
          ) : null}
          {record?.seededPedigree ? (
            <div style={drawerLastUpdatedStyle}>
              <PedigreeTag pedigree={record.seededPedigree} />
            </div>
          ) : null}
        </Flex>
        <Flex vertical align="flex-end" gap={4}>
          <Flex gap={8}>
            <Button type="primary" onClick={onAdjustInventory} disabled={!record || isAdjusting}>
              Adjust Inventory
            </Button>
            {moreMenuItems.length > 0 && (
              <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="bottomRight">
                <Button type="default" disabled={!record || isAdjusting}>
                  {t('seedTracker:detail.more')}
                </Button>
              </Dropdown>
            )}
          </Flex>
          {(record?.seasonYear || record?.lastUpdatedDate) && (
            <Flex align="center" gap={30} style={drawerSeasonRowStyle}>
              {record?.seasonYear && record.seasonYear !== '—' ? (
                <div style={drawerSeasonLabelStyle}>{record.seasonYear} Season</div>
              ) : null}
              {record?.lastUpdatedDate ? (
                <div style={drawerLastUpdatedStyle}>
                  <span style={drawerLastUpdatedLabelStyle}>{t('seedTracker:seedInventoryFields.lastUpdatedOn')}</span>
                  {formatLastUpdatedDate(record.lastUpdatedDate)}
                </div>
              ) : null}
            </Flex>
          )}
        </Flex>
      </Flex>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              horizontalMargin: '0',
            },
          },
        }}
      >
        <SeedInventoryDrawerTabs
          style={seedInventoryTabsStyles}
          tabBarStyle={seedInventoryTabsBarStyles}
          items={tabItems}
          activeKey={activeKey}
          onChange={setActiveKey}
        />
      </ConfigProvider>
    </Flex>
  )
}

export default SeedInventoryDrawerContent
